// src/components/admin/page-management/SectionsTab.js
import axios from "axios";
import { useEffect, useState } from "react";
import PageTemplatesManager from "./PageTemplatesManager"; // Import the new template manager
import SectionForm from "./SectionForm";
import SectionPreview from "./SectionPreview";

const SectionsTab = ({
  page,
  setPage,
  setError,
  setLoading,
  hashTarget, // ADD THIS LINE
  onHashHandled,
}) => {
  const [isAddingSectionType, setIsAddingSectionType] = useState(false);
  const [isEditingSection, setIsEditingSection] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [pageSlug, setPageSlug] = useState("shared");
  const [currentParentId, setCurrentParentId] = useState(null);
  const [isAddingToContainer, setIsAddingToContainer] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false); // State for showing templates

  // Extract and set page slug when the page changes
  useEffect(() => {
    if (page && page.slug) {
      const cleanSlug = page.slug.replace(/[^a-zA-Z0-9-_]/g, "");
      setPageSlug(cleanSlug);
      console.log("Page slug set to:", cleanSlug);
    } else {
      setPageSlug("shared");
      console.log('No page slug found, using "shared"');
    }
  }, [page]);

  // Handle hash navigation from live edit mode
  useEffect(() => {
    if (hashTarget && page) {
      handleHashNavigation(hashTarget);
      if (onHashHandled) {
        onHashHandled(); // Clear the hash after handling
      }
    }
  }, [hashTarget, page]);

  // Available section types - now with slideshow section
  const sectionTypes = [
    { type: "hero", name: "Hero" },
    { type: "headerDivided", name: "Header Divided" },
    { type: "text", name: "Text" },
    { type: "richText", name: "Rich Text" },
    { type: "button", name: "Button" },
    { type: "image", name: "Image" },
    { type: "gallery", name: "Gallery" },
    { type: "cta", name: "Call to Action" },
    { type: "columns", name: "Columns" },
    { type: "spacer", name: "Spacer" },
    { type: "container", name: "Container" },
    { type: "slideshow", name: "Slideshow" },
    { type: "slideshowHeaderWave", name: "SlideshowHeaderWave" },
    { type: "slideshowLinear", name: "SlideshowLinear" },
    { type: "teamMembers", name: "Team Members" },
    { type: "googleMaps", name: "Google Maps" },
    { type: "contactForm", name: "Contact Form" },
    { type: "productCatalog", name: "Product Catalog" },
    { type: "productCatalogVertical", name: "Product Catalog Vertical" },
    { type: "staticContent", name: "Static Content" },
    { type: "featuredProducts", name: "Featured Products" },
    { type: "statistics", name: "Statistics" },
    { type: "blogPosts", name: "Blog Posts" },
    { type: "features", name: "Features" },
    { type: "testimonials", name: "Testimonials" },
    { type: "newsletter", name: "Newsletter" },
    { type: 'popup', name: 'Popup' },

  ];

  // Initialize a new section
  const initSection = (type, parentId = null) => {
    // Calculate order - if we're adding after a specific section, adjust order
    let order = getNextOrder(parentId);

    if (window.addAfterSectionId) {
      const afterSection = page.sections.find(
        (s) => s._id === window.addAfterSectionId
      );
      if (afterSection) {
        order = afterSection.order + 1;
      }
      // Clear the stored ID
      delete window.addAfterSectionId;
    }

    // Set appropriate default content based on section type
    let defaultContent = {};

    if (type === "container") {
      defaultContent = {
        containerType: "standard",
        width: "100%",
        padding: "1rem",
        backgroundColor: "transparent",
      };
    }
    if (type === "slideshow") {
      defaultContent = {
        height: "800px",
        overlayOpacity: "0.6",
        textAlignment: "center",
        transitionSpeed: 5000,
        slides: [
          {
            imageUrl: "/images/s3.jpg",
            imageAlt: "Slideshow Image 1",
            title: "Welcome to Our Company",
            subtitle: "SINCE 1998",
            text: "Your partner for quality solutions",
          },
        ],
      };
    }
    if (type === "contactForm") {
      // Set default form fields for contact form
      defaultContent = {
        title: "Send Us a Message",
        submitButtonText: "Send Message",
        successMessage:
          "Thank you for your message! We'll get back to you soon.",
        errorMessage:
          "Sorry, there was an error sending your message. Please try again.",
        layoutType: "side-by-side",
        backgroundColor: "#1A4B84",
        textColor: "#ffffff",
        buttonColor: "#1A4B84",
        buttonTextColor: "#ffffff",
        showContactInfo: true,
        formFields: [
          {
            id: "name",
            type: "text",
            label: "Your Name",
            placeholder: "Your Name",
            required: true,
            width: "half",
          },
          {
            id: "email",
            type: "email",
            label: "Your Email",
            placeholder: "Your Email",
            required: true,
            width: "half",
          },
          {
            id: "phone",
            type: "tel",
            label: "Phone Number",
            placeholder: "Phone Number (Optional)",
            required: false,
            width: "half",
          },
          {
            id: "company",
            type: "text",
            label: "Company Name",
            placeholder: "Company Name (Optional)",
            required: false,
            width: "half",
          },
          {
            id: "message",
            type: "textarea",
            label: "Your Message",
            placeholder: "Your Message",
            required: true,
            width: "full",
          },
        ],
        contactInfo: {
          phone:
            process.env.REACT_APP_COMPANY_PHONE_NUMBER || "+1 (833) 333-3101",
          email: process.env.REACT_APP_COMPANY_EMAIL || "Info@bagbox.ca",
          address:
            process.env.REACT_APP_COMPANY_ADDRESS ||
            "102-1127 14th St W, North Vancouver, BC, V7P 1J9",
          hours: {
            weekday: "Monday - Friday: 9 AM - 5 PM",
            weekend: "Saturday - Sunday: Closed",
          },
          socialMedia: {
            facebook: "",
            twitter: "",
            instagram: "",
            linkedin: "",
          },
        },
      };
    }
    if (type === 'popup') {
      defaultContent = {
        // Trigger settings
        triggerType: 'button',
        triggerText: 'Open Popup',
        triggerButtonStyle: 'primary',
        triggerButtonSize: 'medium',
        triggerButtonPosition: 'inline',
        triggerButtonColor: '#3b82f6',
        triggerButtonTextColor: '#ffffff',
        triggerButtonHoverColor: '#2563eb',
        triggerButtonBorderRadius: '6px',
        triggerButtonShadow: 'none',
        triggerButtonFullWidth: false,

        // Auto trigger (fixed delay)
        autoTriggerDelay: 3000,
        autoTriggerOnce: true,

        // Popup content
        popupTitle: 'Popup Title',
        popupContent: '<p>This is popup content. You can add any HTML content here.</p>',
        popupType: 'content',
        contentLayout: 'text-only',
        contentTextColor: '#333333',
        contentFontSize: '16px',
        contentAlignment: 'left',
        contentImageWidth: '300px',
        contentImagePosition: 'left',
        contentImageBorderRadius: '8px',
        contentVideoWidth: '400px',
        contentVideoPosition: 'left',
        contentVideoAutoplay: false,
        contentVideoControls: true,

        // Popup dimensions & layout
        popupWidth: '600px',
        popupMaxWidth: '90vw',
        popupHeight: 'auto',
        popupMaxHeight: '90vh',
        popupMinWidth: '300px',
        popupMinHeight: '200px',
        popupPosition: 'center',
        popupZIndex: 1000,

        // Colors and appearance
        backgroundColor: '#ffffff',
        textColor: '#333333',
        overlayColor: 'rgba(0, 0, 0, 0.8)',
        borderRadius: '8px',
        padding: '2rem',

        // Behavior
        closeOnOverlayClick: true,
        closeOnEscape: true,
        showCloseButton: true,

        // Animation
        animationType: 'fade',
        animationDuration: 300,

        // Initialize empty images array
        images: []
      };
    }

    return {
      type,
      order: getNextOrder(parentId),
      content: defaultContent,
      settings: {
        fullWidth: type === "slideshow" ? true : false, // Slideshows are full width by default
      },
      parentId: parentId,
    };
  };

  // Helper to get the next available order value
  const getNextOrder = (parentId = null) => {
    if (!parentId) {
      // Top-level sections
      return page.sections.filter((s) => !s.parentId).length;
    } else {
      // Child sections of a container
      return page.sections.filter(
        (s) => s.parentId && s.parentId.toString() === parentId.toString()
      ).length;
    }
  };

  // Save a new section to the page
  const saveNewSection = async (section) => {
    try {
      setLoading(true);

      // Create a clean copy of the section for creation
      const sectionData = {
        type: section.type,
        order: section.order || getNextOrder(section.parentId),
        content: { ...section.content },
        settings: { ...section.settings },
        parentId: section.parentId,
      };

      console.log("Adding new section with data:", sectionData);

      const response = await axios.post(
        `/api/admin/pages/${page._id}/sections`,
        sectionData
      );
      setPage(response.data);
      setIsAddingSectionType(false);
      setIsAddingToContainer(false);
      setCurrentSection(null);
      setCurrentParentId(null);
    } catch (err) {
      console.error("Error adding section:", err);
      setError(
        "Failed to add section: " + (err.response?.data?.error || err.message)
      );
    } finally {
      setLoading(false);
    }
  };

  // Update an existing section
  const updateSection = async (section) => {
    try {
      setLoading(true);

      // Create a clean copy of the section for update
      const updateData = {
        type: section.type,
        order: section.order,
        content: JSON.parse(JSON.stringify(section.content)), // Deep clone to ensure all data is included
        settings: JSON.parse(JSON.stringify(section.settings)), // Deep clone settings too
      };

      // Handle parent ID if present
      if (section.parentId) {
        updateData.parentId = section.parentId;
      }

      // Add logging to verify what's being sent
      console.log(
        "Updating section with data:",
        JSON.stringify(updateData, null, 2)
      );

      const response = await axios.put(
        `/api/admin/pages/${page._id}/sections/${section._id}`,
        updateData
      );

      // Ensure page state is updated with the latest data from server
      setPage(response.data);
      setIsEditingSection(false);
      setCurrentSection(null);
    } catch (err) {
      console.error("Error updating section:", err);
      setError(
        "Failed to update section: " +
        (err.response?.data?.error || err.message)
      );
    } finally {
      setLoading(false);
    }
  };

  // Delete a section
  const deleteSection = async (sectionId) => {
    if (!window.confirm("Are you sure you want to delete this section?")) {
      return;
    }

    try {
      setLoading(true);

      // Check if this is a container with children
      const section = page.sections.find((s) => s._id === sectionId);
      const hasChildren = page.sections.some(
        (s) => s.parentId && s.parentId.toString() === sectionId.toString()
      );

      if (section && section.type === "container" && hasChildren) {
        const deleteChildren = window.confirm(
          "This container has child sections that will also be deleted. Continue?"
        );
        if (!deleteChildren) {
          setLoading(false);
          return;
        }

        // Delete all children first (could be enhanced to be a single backend call)
        const childrenIds = page.sections
          .filter(
            (s) => s.parentId && s.parentId.toString() === sectionId.toString()
          )
          .map((s) => s._id);

        for (const childId of childrenIds) {
          await axios.delete(
            `/api/admin/pages/${page._id}/sections/${childId}`
          );
        }
      }

      const response = await axios.delete(
        `/api/admin/pages/${page._id}/sections/${sectionId}`
      );
      setPage(response.data);
    } catch (err) {
      console.error("Error deleting section:", err);
      setError("Failed to delete section");
    } finally {
      setLoading(false);
    }
  };

  // Move section up or down
  const moveSection = async (sectionId, direction) => {
    try {
      setLoading(true);

      // Get the section to move
      const section = page.sections.find((s) => s._id === sectionId);
      if (!section) return;

      // Get all sections at the same level (either top-level or with same parent)
      const siblingCondition = section.parentId
        ? (s) =>
          s.parentId && s.parentId.toString() === section.parentId.toString()
        : (s) => !s.parentId;

      // Get sorted sections at this level
      const sortedSections = [...page.sections]
        .filter(siblingCondition)
        .sort((a, b) => a.order - b.order);

      // Find section index
      const sectionIndex = sortedSections.findIndex((s) => s._id === sectionId);
      if (sectionIndex === -1) return;

      // Determine target index
      const targetIndex =
        direction === "up" ? sectionIndex - 1 : sectionIndex + 1;

      // Check boundaries
      if (targetIndex < 0 || targetIndex >= sortedSections.length) {
        setLoading(false);
        return;
      }

      // Create array of section IDs in the new order
      const sectionIds = sortedSections.map((s) => s._id);

      // Swap positions
      [sectionIds[sectionIndex], sectionIds[targetIndex]] = [
        sectionIds[targetIndex],
        sectionIds[sectionIndex],
      ];

      // Update the order on the server
      const response = await axios.post(
        `/api/admin/pages/${page._id}/sections/reorder`,
        {
          sectionIds,
          parentId: section.parentId, // Pass parentId to help identify the right sections
        }
      );

      setPage(response.data);
    } catch (err) {
      console.error("Error reordering sections:", err);
      setError("Failed to reorder sections");
    } finally {
      setLoading(false);
    }
  };

  // Add child section to a container
  const addChildToContainer = (containerId) => {
    setCurrentParentId(containerId);
    setIsAddingToContainer(true);
    setIsAddingSectionType(true);
  };

  // Callback when a template is applied successfully
  const handleTemplateApplied = (templateName, updatedPageData) => {
    setShowTemplates(false);
    setError(null);

    // Update the page state with the new data if available
    if (updatedPageData) {
      setPage(updatedPageData);
    }
  };

  // Handle hash navigation
  const handleHashNavigation = (hash) => {
    console.log("Handling hash navigation:", hash);

    if (hash.startsWith("#section-")) {
      // Edit specific section
      const sectionId = hash.replace("#section-", "");
      const section = page.sections.find((s) => s._id === sectionId);

      if (section) {
        console.log("Found section to edit:", section);
        setCurrentSection({ ...section });
        setIsEditingSection(true);
        setIsAddingSectionType(false);
        setShowTemplates(false);

        // Scroll to the section form or show a notification
        setTimeout(() => {
          const formElement = document.querySelector(".section-form-container");
          if (formElement) {
            formElement.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
      } else {
        console.warn("Section not found:", sectionId);
        setError(`Section not found. It may have been deleted.`);
      }
    } else if (hash === "#add-section") {
      // Add new section
      console.log("Adding new section from hash");
      setIsAddingSectionType(true);
      setIsEditingSection(false);
      setCurrentSection(null);
      setShowTemplates(false);
      setCurrentParentId(null);
      setIsAddingToContainer(false);

      // Scroll to section type selector
      setTimeout(() => {
        const selectorElement = document.querySelector(
          ".section-type-selector"
        );
        if (selectorElement) {
          selectorElement.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else if (hash.startsWith("#add-section-after-")) {
      // Add section after a specific section
      const afterSectionId = hash.replace("#add-section-after-", "");
      const afterSection = page.sections.find((s) => s._id === afterSectionId);

      if (afterSection) {
        console.log("Adding section after:", afterSection);
        setIsAddingSectionType(true);
        setIsEditingSection(false);
        setCurrentSection(null);
        setShowTemplates(false);
        setCurrentParentId(afterSection.parentId || null);
        setIsAddingToContainer(!!afterSection.parentId);

        // Store the "after" section info for ordering
        window.addAfterSectionId = afterSectionId;

        setTimeout(() => {
          const selectorElement = document.querySelector(
            ".section-type-selector"
          );
          if (selectorElement) {
            selectorElement.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
      }
    }
  };

  // Render a section item with its child sections (if any)
  const renderSectionItem = (section, index, parentSections) => {
    // Find child sections if this is a container
    const childSections =
      section.type === "container"
        ? page.sections
          .filter(
            (s) =>
              s.parentId && s.parentId.toString() === section._id.toString()
          )
          .sort((a, b) => a.order - b.order)
        : [];

    return (
      <div key={section._id} className="border rounded p-4 bg-white mb-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <div className="mr-2">
              <span className="px-2 py-1 bg-gray-100 rounded text-xs font-medium">
                {index + 1}
              </span>
            </div>
            <div>
              <h4 className="font-medium capitalize">{section.type}</h4>
              <SectionPreview section={section} />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="flex flex-col space-y-1">
              <button
                className="p-1 text-gray-600 hover:text-gray-800 disabled:opacity-30"
                style={{ boxShadow: "none" }}
                onClick={() => moveSection(section._id, "up")}
                disabled={index === 0}
                title="Move Up"
              >
                ▲
              </button>
              <button
                className="p-1 text-gray-600 hover:text-gray-800 disabled:opacity-30"
                style={{ boxShadow: "none" }}
                onClick={() => moveSection(section._id, "down")}
                disabled={index === parentSections.length - 1}
                title="Move Down"
              >
                ▼
              </button>
            </div>
            <button
              className="p-2 text-gray-600 hover:text-gray-800"
              style={{ boxShadow: "none", transform: "none" }}
              onClick={() => {
                setCurrentSection({ ...section });
                setIsEditingSection(true);
              }}
            >
              Edit
            </button>
            {section.type === "container" && (
              <button
                className="p-2 text-blue-600 hover:text-blue-800"
                style={{ boxShadow: "none", transform: "none" }}
                onClick={() => addChildToContainer(section._id)}
              >
                Add Child
              </button>
            )}
            <button
              className="p-2 text-red-600 hover:text-red-800"
              style={{ boxShadow: "none", transform: "none" }}
              onClick={() => deleteSection(section._id)}
            >
              Delete
            </button>
          </div>
        </div>

        {/* Render child sections if this is a container */}
        {section.type === "container" && childSections.length > 0 && (
          <div className="mt-4 pl-6 border-l-2 border-blue-200">
            <div className="text-sm font-medium text-gray-500 mb-2">
              Child Sections:
            </div>
            {childSections.map((childSection, childIndex) =>
              renderSectionItem(childSection, childIndex, childSections)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {showTemplates ? (
        <div>
          <div className="flex justify-between mb-6">
            <h3 className="text-xl font-semibold">Page Templates</h3>
            <button
              className="text-gray-600 hover:text-gray-800 px-4 py-2 rounded"
              onClick={() => setShowTemplates(false)}
            >
              Back to Sections
            </button>
          </div>

          {/* Use our new PageTemplatesManager instead of the old implementation */}
          <PageTemplatesManager
            pageId={page._id}
            onTemplateApplied={handleTemplateApplied}
            setError={setError}
          />
        </div>
      ) : isAddingSectionType ? (
        <div className="section-type-selector">
          <h3 className="text-xl font-semibold mb-4">
            {isAddingToContainer
              ? `Add Section to Container`
              : "Add New Section"}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {sectionTypes.map((section) => (
              <button
                key={section.type}
                className="p-4 border rounded hover:bg-gray-50"
                onClick={() => {
                  setCurrentSection(initSection(section.type, currentParentId));
                  setIsAddingSectionType(false);
                }}
              >
                {section.name}
              </button>
            ))}
          </div>
          <div className="mt-4">
            <button
              className="text-gray-600 hover:text-gray-800"
              onClick={() => {
                setIsAddingSectionType(false);
                setCurrentParentId(null);
                setIsAddingToContainer(false);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : currentSection ? (
        <div className="section-form-container">
          <h3 className="text-xl font-semibold mb-4">
            {isEditingSection
              ? "Edit Section"
              : isAddingToContainer
                ? "Add Section to Container"
                : "Add Section"}
          </h3>
          <SectionForm
            section={currentSection}
            setSection={setCurrentSection}
            onCancel={() => {
              setCurrentSection(null);
              setIsEditingSection(false);
              setCurrentParentId(null);
              setIsAddingToContainer(false);
            }}
            onSave={() => {
              if (isEditingSection) {
                updateSection(currentSection);
              } else {
                saveNewSection(currentSection);
              }
            }}
            buttonText={isEditingSection ? "Update Section" : "Add Section"}
            pageSlug={pageSlug}
          />
        </div>
      ) : (
        <div>
          <div className="flex flex-wrap justify-between items-center mb-6 gap-2">
            <h3 className="text-xl font-semibold">Page Sections</h3>
            <div className="flex space-x-2">
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                onClick={() => setShowTemplates(true)}
              >
                Use Template
              </button>
              <button
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
                onClick={() => setIsAddingSectionType(true)}
              >
                Add Section
              </button>
            </div>
          </div>

          {page.sections && page.sections.length > 0 ? (
            <div className="space-y-2">
              {page.sections
                .filter((section) => !section.parentId) // Only get top-level sections
                .sort((a, b) => a.order - b.order)
                .map((section, index, topSections) =>
                  renderSectionItem(section, index, topSections)
                )}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded">
              <p className="text-gray-500 mb-4">
                No sections yet. Add your first section or use a template to
                start building the page.
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                  onClick={() => setShowTemplates(true)}
                >
                  Use Template
                </button>
                <button
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
                  onClick={() => setIsAddingSectionType(true)}
                >
                  Add Section
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SectionsTab;

// // before live edit
// // src/components/admin/page-management/SectionsTab.js
// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import SectionForm from './SectionForm';
// import SectionPreview from './SectionPreview';
// import PageTemplatesManager from './PageTemplatesManager'; // Import the new template manager

// const SectionsTab = ({ page, setPage, setError, setLoading }) => {
//   const [isAddingSectionType, setIsAddingSectionType] = useState(false);
//   const [isEditingSection, setIsEditingSection] = useState(false);
//   const [currentSection, setCurrentSection] = useState(null);
//   const [pageSlug, setPageSlug] = useState('shared');
//   const [currentParentId, setCurrentParentId] = useState(null);
//   const [isAddingToContainer, setIsAddingToContainer] = useState(false);
//   const [showTemplates, setShowTemplates] = useState(false); // State for showing templates

//   // Extract and set page slug when the page changes
//   useEffect(() => {
//     if (page && page.slug) {
//       const cleanSlug = page.slug.replace(/[^a-zA-Z0-9-_]/g, '');
//       setPageSlug(cleanSlug);
//       console.log('Page slug set to:', cleanSlug);
//     } else {
//       setPageSlug('shared');
//       console.log('No page slug found, using "shared"');
//     }
//   }, [page]);

//   // Available section types - now with slideshow section
//   const sectionTypes = [
//     { type: 'hero', name: 'Hero' },
//     { type: 'headerDivided', name: 'Header Divided' },
//     { type: 'text', name: 'Text' },
//     { type: 'richText', name: 'Rich Text' },
//     { type: 'button', name: 'Button' },
//     { type: 'image', name: 'Image' },
//     { type: 'gallery', name: 'Gallery' },
//     { type: 'cta', name: 'Call to Action' },
//     { type: 'columns', name: 'Columns' },
//     { type: 'spacer', name: 'Spacer' },
//     { type: 'container', name: 'Container' },
//     { type: 'slideshow', name: 'Slideshow' },
//     { type: 'slideshowHeaderWave', name: 'SlideshowHeaderWave' },
//     { type: 'slideshowLinear', name: 'SlideshowLinear' },
//     { type: 'teamMembers', name: 'Team Members' },
//     { type: 'googleMaps', name: 'Google Maps' },
//     { type: 'contactForm', name: 'Contact Form'},
//     { type: 'productCatalog', name: 'Product Catalog' },
//     { type: 'productCatalogVertical', name: 'Product Catalog Vertical' },
//     { type: 'staticContent', name: 'Static Content' },
//     { type: 'featuredProducts', name: 'Featured Products' },
//     { type: 'statistics', name: 'Statistics' },
//     { type: 'blogPosts', name: 'Blog Posts' },
//     { type: 'features', name: 'Features' },
//     { type: 'testimonials', name: 'Testimonials' },
//     { type: 'newsletter', name: 'Newsletter' },
//   ];

//   // Initialize a new section
//   const initSection = (type, parentId = null) => {
//     // Set appropriate default content based on section type
//     let defaultContent = {};

//     if (type === 'container') {
//       defaultContent = {
//         containerType: 'standard',
//         width: '100%',
//         padding: '1rem',
//         backgroundColor: 'transparent'
//       };
//     } if (type === 'slideshow') {
//       defaultContent = {
//         height: '800px',
//         overlayOpacity: '0.6',
//         textAlignment: 'center',
//         transitionSpeed: 5000,
//         slides: [
//           {
//             imageUrl: '/images/s3.jpg',
//             imageAlt: 'Slideshow Image 1',
//             title: 'Welcome to Our Company',
//             subtitle: 'SINCE 1998',
//             text: 'Your partner for quality solutions'
//           }
//         ]
//       };
//     } if (type === 'contactForm') {
//       // Set default form fields for contact form
//       defaultContent = {
//         title: 'Send Us a Message',
//         submitButtonText: 'Send Message',
//         successMessage: "Thank you for your message! We'll get back to you soon.",
//         errorMessage: "Sorry, there was an error sending your message. Please try again.",
//         layoutType: 'side-by-side',
//         backgroundColor: '#1A4B84',
//         textColor: '#ffffff',
//         buttonColor: '#1A4B84',
//         buttonTextColor: '#ffffff',
//         showContactInfo: true,
//         formFields: [
//           {
//             id: 'name',
//             type: 'text',
//             label: 'Your Name',
//             placeholder: 'Your Name',
//             required: true,
//             width: 'half'
//           },
//           {
//             id: 'email',
//             type: 'email',
//             label: 'Your Email',
//             placeholder: 'Your Email',
//             required: true,
//             width: 'half'
//           },
//           {
//             id: 'phone',
//             type: 'tel',
//             label: 'Phone Number',
//             placeholder: 'Phone Number (Optional)',
//             required: false,
//             width: 'half'
//           },
//           {
//             id: 'company',
//             type: 'text',
//             label: 'Company Name',
//             placeholder: 'Company Name (Optional)',
//             required: false,
//             width: 'half'
//           },
//           {
//             id: 'message',
//             type: 'textarea',
//             label: 'Your Message',
//             placeholder: 'Your Message',
//             required: true,
//             width: 'full'
//           }
//         ],
//         contactInfo: {
//           phone: process.env.REACT_APP_COMPANY_PHONE_NUMBER || "+1 (833) 333-3101",
//           email: process.env.REACT_APP_COMPANY_EMAIL || "Info@bagbox.ca",
//           address: process.env.REACT_APP_COMPANY_ADDRESS || "102-1127 14th St W, North Vancouver, BC, V7P 1J9",
//           hours: {
//             weekday: "Monday - Friday: 9 AM - 5 PM",
//             weekend: "Saturday - Sunday: Closed"
//           },
//           socialMedia: {
//             facebook: "",
//             twitter: "",
//             instagram: "",
//             linkedin: ""
//           }
//         }
//       };
//     }

//     return {
//       type,
//       order: getNextOrder(parentId),
//       content: defaultContent,
//       settings: {
//         fullWidth: type === 'slideshow' ? true : false // Slideshows are full width by default
//       },
//       parentId: parentId
//     };
//   };

//   // Helper to get the next available order value
//   const getNextOrder = (parentId = null) => {
//     if (!parentId) {
//       // Top-level sections
//       return page.sections.filter(s => !s.parentId).length;
//     } else {
//       // Child sections of a container
//       return page.sections.filter(s => s.parentId && s.parentId.toString() === parentId.toString()).length;
//     }
//   };

//   // Save a new section to the page
//   const saveNewSection = async (section) => {
//     try {
//       setLoading(true);

//       // Create a clean copy of the section for creation
//       const sectionData = {
//         type: section.type,
//         order: section.order || getNextOrder(section.parentId),
//         content: { ...section.content },
//         settings: { ...section.settings },
//         parentId: section.parentId
//       };

//       console.log('Adding new section with data:', sectionData);

//       const response = await axios.post(`/api/admin/pages/${page._id}/sections`, sectionData);
//       setPage(response.data);
//       setIsAddingSectionType(false);
//       setIsAddingToContainer(false);
//       setCurrentSection(null);
//       setCurrentParentId(null);
//     } catch (err) {
//       console.error('Error adding section:', err);
//       setError('Failed to add section: ' + (err.response?.data?.error || err.message));
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Update an existing section
//   const updateSection = async (section) => {
//     try {
//       setLoading(true);

//       // Create a clean copy of the section for update
//       const updateData = {
//         type: section.type,
//         order: section.order,
//         content: JSON.parse(JSON.stringify(section.content)), // Deep clone to ensure all data is included
//         settings: JSON.parse(JSON.stringify(section.settings)) // Deep clone settings too
//       };

//       // Handle parent ID if present
//       if (section.parentId) {
//         updateData.parentId = section.parentId;
//       }

//       // Add logging to verify what's being sent
//       console.log('Updating section with data:', JSON.stringify(updateData, null, 2));

//       const response = await axios.put(
//         `/api/admin/pages/${page._id}/sections/${section._id}`,
//         updateData
//       );

//       // Ensure page state is updated with the latest data from server
//       setPage(response.data);
//       setIsEditingSection(false);
//       setCurrentSection(null);
//     } catch (err) {
//       console.error('Error updating section:', err);
//       setError('Failed to update section: ' + (err.response?.data?.error || err.message));
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Delete a section
//   const deleteSection = async (sectionId) => {
//     if (!window.confirm('Are you sure you want to delete this section?')) {
//       return;
//     }

//     try {
//       setLoading(true);

//       // Check if this is a container with children
//       const section = page.sections.find(s => s._id === sectionId);
//       const hasChildren = page.sections.some(s => s.parentId && s.parentId.toString() === sectionId.toString());

//       if (section && section.type === 'container' && hasChildren) {
//         const deleteChildren = window.confirm('This container has child sections that will also be deleted. Continue?');
//         if (!deleteChildren) {
//           setLoading(false);
//           return;
//         }

//         // Delete all children first (could be enhanced to be a single backend call)
//         const childrenIds = page.sections
//           .filter(s => s.parentId && s.parentId.toString() === sectionId.toString())
//           .map(s => s._id);

//         for (const childId of childrenIds) {
//           await axios.delete(`/api/admin/pages/${page._id}/sections/${childId}`);
//         }
//       }

//       const response = await axios.delete(
//         `/api/admin/pages/${page._id}/sections/${sectionId}`
//       );
//       setPage(response.data);
//     } catch (err) {
//       console.error('Error deleting section:', err);
//       setError('Failed to delete section');
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Move section up or down
//   const moveSection = async (sectionId, direction) => {
//     try {
//       setLoading(true);

//       // Get the section to move
//       const section = page.sections.find(s => s._id === sectionId);
//       if (!section) return;

//       // Get all sections at the same level (either top-level or with same parent)
//       const siblingCondition = section.parentId
//         ? s => s.parentId && s.parentId.toString() === section.parentId.toString()
//         : s => !s.parentId;

//       // Get sorted sections at this level
//       const sortedSections = [...page.sections]
//         .filter(siblingCondition)
//         .sort((a, b) => a.order - b.order);

//       // Find section index
//       const sectionIndex = sortedSections.findIndex(s => s._id === sectionId);
//       if (sectionIndex === -1) return;

//       // Determine target index
//       const targetIndex = direction === 'up' ? sectionIndex - 1 : sectionIndex + 1;

//       // Check boundaries
//       if (targetIndex < 0 || targetIndex >= sortedSections.length) {
//         setLoading(false);
//         return;
//       }

//       // Create array of section IDs in the new order
//       const sectionIds = sortedSections.map(s => s._id);

//       // Swap positions
//       [sectionIds[sectionIndex], sectionIds[targetIndex]] =
//         [sectionIds[targetIndex], sectionIds[sectionIndex]];

//       // Update the order on the server
//       const response = await axios.post(`/api/admin/pages/${page._id}/sections/reorder`, {
//         sectionIds,
//         parentId: section.parentId // Pass parentId to help identify the right sections
//       });

//       setPage(response.data);
//     } catch (err) {
//       console.error('Error reordering sections:', err);
//       setError('Failed to reorder sections');
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Add child section to a container
//   const addChildToContainer = (containerId) => {
//     setCurrentParentId(containerId);
//     setIsAddingToContainer(true);
//     setIsAddingSectionType(true);
//   };

//   // Callback when a template is applied successfully
//   const handleTemplateApplied = (templateName, updatedPageData) => {
//     setShowTemplates(false);
//     setError(null);

//     // Update the page state with the new data if available
//     if (updatedPageData) {
//       setPage(updatedPageData);
//     }
//   };

//   // Render a section item with its child sections (if any)
//   const renderSectionItem = (section, index, parentSections) => {
//     // Find child sections if this is a container
//     const childSections = section.type === 'container'
//       ? page.sections
//           .filter(s => s.parentId && s.parentId.toString() === section._id.toString())
//           .sort((a, b) => a.order - b.order)
//       : [];

//     return (
//       <div
//         key={section._id}
//         className="border rounded p-4 bg-white mb-2"
//       >
//         <div className="flex justify-between items-center">
//           <div className="flex items-center">
//             <div className="mr-2">
//               <span className="px-2 py-1 bg-gray-100 rounded text-xs font-medium">
//                 {index + 1}
//               </span>
//             </div>
//             <div>
//               <h4 className="font-medium capitalize">{section.type}</h4>
//               <SectionPreview section={section} />
//             </div>
//           </div>
//           <div className="flex items-center space-x-2">
//             <div className="flex flex-col space-y-1">
//               <button
//                 className="p-1 text-gray-600 hover:text-gray-800 disabled:opacity-30"
//                 style={{boxShadow:'none'}}
//                 onClick={() => moveSection(section._id, 'up')}
//                 disabled={index === 0}
//                 title="Move Up"
//               >
//                 ▲
//               </button>
//               <button
//                 className="p-1 text-gray-600 hover:text-gray-800 disabled:opacity-30"
//                 style={{boxShadow:'none'}}
//                 onClick={() => moveSection(section._id, 'down')}
//                 disabled={index === parentSections.length - 1}
//                 title="Move Down"
//               >
//                 ▼
//               </button>
//             </div>
//             <button
//               className="p-2 text-gray-600 hover:text-gray-800"
//               style={{boxShadow:'none', transform:'none'}}
//               onClick={() => {
//                 setCurrentSection({ ...section });
//                 setIsEditingSection(true);
//               }}
//             >
//               Edit
//             </button>
//             {section.type === 'container' && (
//               <button
//                 className="p-2 text-blue-600 hover:text-blue-800"
//                 style={{boxShadow:'none', transform:'none'}}
//                 onClick={() => addChildToContainer(section._id)}
//               >
//                 Add Child
//               </button>
//             )}
//             <button
//               className="p-2 text-red-600 hover:text-red-800"
//               style={{boxShadow:'none', transform:'none'}}
//               onClick={() => deleteSection(section._id)}
//             >
//               Delete
//             </button>
//           </div>
//         </div>

//         {/* Render child sections if this is a container */}
//         {section.type === 'container' && childSections.length > 0 && (
//           <div className="mt-4 pl-6 border-l-2 border-blue-200">
//             <div className="text-sm font-medium text-gray-500 mb-2">Child Sections:</div>
//             {childSections.map((childSection, childIndex) =>
//               renderSectionItem(childSection, childIndex, childSections)
//             )}
//           </div>
//         )}
//       </div>
//     );
//   };

//   return (
//     <div>
//       {showTemplates ? (
//         <div>
//           <div className="flex justify-between mb-6">
//             <h3 className="text-xl font-semibold">Page Templates</h3>
//             <button
//               className="text-gray-600 hover:text-gray-800 px-4 py-2 rounded"
//               onClick={() => setShowTemplates(false)}
//             >
//               Back to Sections
//             </button>
//           </div>

//           {/* Use our new PageTemplatesManager instead of the old implementation */}
//           <PageTemplatesManager
//             pageId={page._id}
//             onTemplateApplied={handleTemplateApplied}
//             setError={setError}
//           />
//         </div>
//       ) : isAddingSectionType ? (
//         <div>
//           <h3 className="text-xl font-semibold mb-4">
//             {isAddingToContainer
//               ? `Add Section to Container`
//               : 'Add New Section'}
//           </h3>
//           <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
//             {sectionTypes.map(section => (
//               <button
//                 key={section.type}
//                 className="p-4 border rounded hover:bg-gray-50"
//                 onClick={() => {
//                   setCurrentSection(initSection(section.type, currentParentId));
//                   setIsAddingSectionType(false);
//                 }}
//               >
//                 {section.name}
//               </button>
//             ))}
//           </div>
//           <div className="mt-4">
//             <button
//               className="text-gray-600 hover:text-gray-800"
//               onClick={() => {
//                 setIsAddingSectionType(false);
//                 setCurrentParentId(null);
//                 setIsAddingToContainer(false);
//               }}
//             >
//               Cancel
//             </button>
//           </div>
//         </div>
//       ) : currentSection ? (
//         <div>
//           <h3 className="text-xl font-semibold mb-4">
//             {isEditingSection ? 'Edit Section' :
//              isAddingToContainer ? 'Add Section to Container' : 'Add Section'}
//           </h3>
//           <SectionForm
//             section={currentSection}
//             setSection={setCurrentSection}
//             onCancel={() => {
//               setCurrentSection(null);
//               setIsEditingSection(false);
//               setCurrentParentId(null);
//               setIsAddingToContainer(false);
//             }}
//             onSave={() => {
//               if (isEditingSection) {
//                 updateSection(currentSection);
//               } else {
//                 saveNewSection(currentSection);
//               }
//             }}
//             buttonText={isEditingSection ? 'Update Section' : 'Add Section'}
//             pageSlug={pageSlug}
//           />
//         </div>
//       ) : (
//         <div>
//           <div className="flex flex-wrap justify-between items-center mb-6 gap-2">
//             <h3 className="text-xl font-semibold">Page Sections</h3>
//             <div className="flex space-x-2">
//               <button
//                 className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
//                 onClick={() => setShowTemplates(true)}
//               >
//                 Use Template
//               </button>
//               <button
//                 className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
//                 onClick={() => setIsAddingSectionType(true)}
//               >
//                 Add Section
//               </button>
//             </div>
//           </div>

//           {page.sections && page.sections.length > 0 ? (
//             <div className="space-y-2">
//               {page.sections
//                 .filter(section => !section.parentId) // Only get top-level sections
//                 .sort((a, b) => a.order - b.order)
//                 .map((section, index, topSections) =>
//                   renderSectionItem(section, index, topSections)
//                 )}
//             </div>
//           ) : (
//             <div className="text-center py-8 bg-gray-50 rounded">
//               <p className="text-gray-500 mb-4">No sections yet. Add your first section or use a template to start building the page.</p>
//               <div className="flex justify-center space-x-4">
//                 <button
//                   className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
//                   onClick={() => setShowTemplates(true)}
//                 >
//                   Use Template
//                 </button>
//                 <button
//                   className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
//                   onClick={() => setIsAddingSectionType(true)}
//                 >
//                   Add Section
//                 </button>
//               </div>
//             </div>
//           )}
//         </div>
//       )}
//     </div>
//   );
// };

// export default SectionsTab;
